import React from "react";

import {
	Button,
	ChipInput,
	ColorPickerField,
	Input,
	NumberInput,
	TextArea,
	Title,
	Toggle,
} from "@amphore-dev/amphore-lib";
import { Formik, Form, FormikProps } from "formik";
import { t } from "i18next";

import { useAuth } from "@/hooks/useAuth";
import { useCanCreateInWorkspace } from "@/hooks/useCanCreateInWorkspace";

import { ClientSelect } from "@/components/molecules/ClientSelect/ClientSelect";
import { WorkspacePicker } from "@/components/organisms/WorkspacePicker/WorkspacePicker";

import { fieldError } from "@/utils/UForms";
import { MANAGING_ROLES } from "@/utils/UWorkspaces";

import { TICKET_PREFIXES_CHIP_PROPS } from "@/constants/CProjects";

import { TCompany, TProjectEditFormValues } from "@/types";

import {
	ProjectEditFormInitialValues,
	ProjectEditFormValidation,
} from "./ProjectEditFormValidation";

interface IProjectEditFormProps {
	initialValues?: Partial<TProjectEditFormValues>;
	onSubmit: (
		values: TProjectEditFormValues,
		formikHelpers: unknown
	) => Promise<unknown>;
	innerRef?: React.Ref<FormikProps<TProjectEditFormValues>>;
	/**
	 * Creating: the clients of every workspace the user adds projects to,
	 * and a choice of workspace for an internal project or a new client
	 * (shown once there is more than one to choose from).
	 */
	withWorkspace?: boolean;
}

/**
 * Identity, then who the work is for: an existing client, a new one typed
 * in place (created on submit, see toProjectPayload), or none for an
 * internal project. The rate is the project's override of the client's.
 */
export const ProjectEditForm: React.FC<IProjectEditFormProps> = ({
	initialValues,
	onSubmit,
	innerRef,
	withWorkspace = false,
}) => {
	const { user } = useAuth();
	const { canCreateIn, creatable } = useCanCreateInWorkspace();
	const managing = (user?.workspaces ?? []).filter((w) =>
		MANAGING_ROLES.includes(w.role)
	);

	return (
		<Formik
			initialValues={{
				...ProjectEditFormInitialValues,
				...initialValues,
			}}
			onSubmit={onSubmit}
			validationSchema={ProjectEditFormValidation()}
			innerRef={innerRef}
		>
			{(formik) => {
				const { values, errors, touched, setFieldValue } = formik;
				const clientError =
					touched.client || touched.newClientName
						? ((errors.client as string | undefined) ??
							errors.newClientName)
						: undefined;

				return (
					<Form className="col h-full [&>div]:mb-6 [&>div]:flex [&>div]:flex-col [&>div]:gap-4 [&>div]:pb-8 [&>div:not(:last-child)]:border-b">
						<div>
							<Title size="h4">
								{t("forms.project.identity")}
							</Title>
							<div className="row w-full items-center gap-4">
								<ColorPickerField
									value={values.color || "#000000"}
									onChange={(color: string) =>
										setFieldValue("color", color)
									}
								/>
								<div className="w-full">
									<Input
										value={values.name}
										onChange={(value) =>
											setFieldValue("name", value)
										}
										placeholder={t(
											"projects.namePlaceholder"
										)}
										error={fieldError(formik, "name")}
										className="w-full"
									/>
								</div>
							</div>
							<TextArea
								name="description"
								error={fieldError(formik, "description")}
								label={t("projects.description")}
								placeholder={t("forms.optional")}
								value={values.description ?? ""}
								onChange={(value) =>
									setFieldValue("description", value)
								}
								rows={3}
							/>
							<ChipInput
								name="ticketPrefixes"
								error={fieldError(formik, "ticketPrefixes")}
								label={t("projects.tickets.prefixes")}
								placeholder={t("projects.tickets.placeholder")}
								value={values.ticketPrefixes}
								onChange={(value) =>
									setFieldValue("ticketPrefixes", value)
								}
								{...TICKET_PREFIXES_CHIP_PROPS}
							/>
						</div>
						<div>
							<Title size="h4">
								{t("projects.client.title")}
							</Title>
							{withWorkspace &&
								(values.internal || values.isNewClient) && (
									<WorkspacePicker
										options={
											values.isNewClient
												? managing
												: creatable
										}
										value={
											values.workspaceId ??
											creatable.find(
												(w) => w.role === "OWNER"
											)?.id
										}
										onChange={(id) =>
											setFieldValue("workspaceId", id)
										}
										className="max-w-none"
									/>
								)}
							<Toggle
								name="internal"
								label={t("projects.client.internal")}
								checked={values.internal}
								onChange={(checked) =>
									setFieldValue("internal", checked)
								}
							/>
							{!values.internal && (
								<>
									{values.isNewClient ? (
										<Input
											name="newClientName"
											label={t("projects.client.newName")}
											placeholder={t(
												"projects.client.newPlaceholder"
											)}
											value={values.newClientName}
											onChange={(value) =>
												setFieldValue(
													"newClientName",
													value
												)
											}
											error={clientError}
											autoComplete="organization"
										/>
									) : (
										<ClientSelect
											filter={
												withWorkspace
													? (client) =>
															canCreateIn(
																client.workspace
																	?.id
															)
													: undefined
											}
											value={values.client}
											onChange={(value) =>
												setFieldValue(
													"client",
													(value as TCompany | null) ??
														null
												)
											}
											error={clientError}
										/>
									)}
									<Button
										variant="ghost"
										size="sm"
										className="self-start"
										onClick={() => {
											setFieldValue(
												"isNewClient",
												!values.isNewClient
											);
											setFieldValue("client", null);
											setFieldValue("newClientName", "");
										}}
									>
										{values.isNewClient
											? t("projects.client.pickExisting")
											: t("projects.client.createNew")}
									</Button>
									<NumberInput
										name="rate"
										error={fieldError(formik, "rate")}
										label={t("projects.client.rate")}
										value={
											values.rate === ""
												? null
												: Number(values.rate)
										}
										onChange={(value) =>
											setFieldValue(
												"rate",
												value === null
													? ""
													: String(value)
											)
										}
										min={0}
										decimals={2}
										after="€"
										placeholder={t(
											"projects.client.rateHint"
										)}
									/>
								</>
							)}
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};
